/**
 * /app/sequences — Multi-channel cadences (SMS + Email + Tasks).
 *
 * Tabs:
 *   Sequences  → list/create/edit, enable toggle
 *   Templates  → 5 prebuilt cadences, clone to start
 *   Enrollments→ per-sequence enrolled leads + pause/resume/cancel
 *   Analytics  → reply rate + per-step delivery counts
 *
 * Paywalled at Pro+ (3 active) and unlimited+AI at Hustler+.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Zap, Mail, MessageSquare, ListChecks, Clock, Plus, Play, Pause, Trash2,
  X, ChevronRight, Loader2, Sparkles, ArrowRight, Send, GitBranch,
  CheckCircle2, AlertCircle, Wand2, Users, Copy, BarChart3, Layers,
  SkipForward, CircleSlash, FileText, UserPlus,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

const CHANNEL_META = {
  sms:   { label: "SMS",   icon: MessageSquare, color: "bg-blue-100 text-blue-700",     accent: "border-blue-400" },
  email: { label: "Email", icon: Mail,          color: "bg-emerald-100 text-emerald-700", accent: "border-emerald-400" },
  task:  { label: "Call Task", icon: ListChecks, color: "bg-amber-100 text-amber-700",  accent: "border-amber-400" },
  wait:  { label: "Wait",  icon: Clock,         color: "bg-[var(--bg-2)] text-[var(--ink-3)]", accent: "border-[var(--line)]" },
};

const STATUS_META = {
  active:        { label: "Active",          color: "text-emerald-700 bg-emerald-50" },
  paused:        { label: "Paused",          color: "text-amber-700 bg-amber-50" },
  completed:     { label: "Completed",       color: "text-[var(--ink-3)] bg-[var(--bg-2)]" },
  exited_reply:  { label: "Replied — exited", color: "text-blue-700 bg-blue-50" },
  exited_stage:  { label: "Stage exited",    color: "text-[var(--ink-3)] bg-[var(--bg-2)]" },
  exited_manual: { label: "Cancelled",       color: "text-[var(--ink-3)] bg-[var(--bg-2)]" },
};

const STAGES = ["New Lead", "Contacted", "Analyzed", "Hot — Buyer Interested",
                "Offer Sent", "Under Contract", "Closed", "Dead"];
const SOURCES = ["Direct Mail", "PPC", "Driving for Dollars", "Referral",
                 "Cold Call", "SMS", "Probate", "Other"];


export default function Sequences() {
  const { user } = useAuth();
  const [tab, setTab] = useState("sequences");
  const [loading, setLoading] = useState(true);
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [limit, setLimit] = useState(0);
  const [aiAllowed, setAiAllowed] = useState(false);
  const [editing, setEditing] = useState(null);          // sequence being edited, 'new', or { aiDraft }
  const [enrollFor, setEnrollFor] = useState(null);       // open enrollments drawer
  const [analyticsFor, setAnalyticsFor] = useState(null);  // open analytics drawer
  const [showAI, setShowAI] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sequences");
      setSequences(data.sequences || []);
      setLimit(data.limit);
      setAiAllowed(!!data.ai_allowed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load sequences");
    }
    setLoading(false);
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/sequences/templates");
      setTemplates(data.templates || []);
    } catch (e) {
      // best-effort
    }
  }, []);

  useEffect(() => {
    track("sequences_page_view");
    load();
    loadTemplates();
  }, [load, loadTemplates]);

  const isPaywalled = limit === 0;
  const activeCount = sequences.filter((s) => s.enabled).length;

  return (
    <div className="space-y-6" data-testid="sequences-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">Multi-channel cadences</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Sequences — speed wins deals</h1>
          <p className="text-[var(--ink-2)] mt-1 max-w-2xl">
            Stack SMS, Email and Call Tasks in one cadence. Triggered on new leads or stage changes,
            auto-exits on reply, with branching paths for the cold ones.
          </p>
        </div>
        {!isPaywalled && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-outline"
              onClick={() => setTab("templates")}
              data-testid="sequences-templates-btn"
            >
              <Layers size={14} /> Templates
            </button>
            {aiAllowed && (
              <button
                type="button"
                className="btn-outline"
                onClick={() => setShowAI(true)}
                data-testid="sequences-ai-btn"
              >
                <Wand2 size={14} /> AI Builder
              </button>
            )}
            <button
              type="button"
              className="btn-money"
              onClick={() => setEditing("new")}
              data-testid="sequences-new-btn"
            >
              <Plus size={14} /> New Sequence
            </button>
          </div>
        )}
      </div>

      {isPaywalled ? (
        <Paywall />
      ) : (
        <>
          {/* Usage strip */}
          <div className="card p-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm" data-testid="sequences-usage-strip">
            <Stat label="Active sequences" value={`${activeCount}${limit === -1 ? "" : ` / ${limit}`}`} />
            <Stat label="Total" value={sequences.length} />
            <Stat label="AI personalization" value={aiAllowed ? "Enabled" : "Hustler+"} muted={!aiAllowed} />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-[var(--line)]">
            {[
              { id: "sequences", label: "Your Sequences", testid: "tab-sequences" },
              { id: "templates", label: "Templates", testid: "tab-templates" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={t.testid}
                className={`px-4 py-2 text-sm font-bold transition-colors border-b-2 -mb-px ${
                  tab === t.id
                    ? "border-emerald-500 text-emerald-700"
                    : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Sequences tab */}
          {tab === "sequences" && (
            loading ? (
              <div className="card text-center py-16 text-[var(--ink-2)]">
                <Loader2 size={20} className="animate-spin mx-auto mb-3" /> Loading…
              </div>
            ) : sequences.length === 0 ? (
              <EmptyState onNew={() => setEditing("new")} onTemplates={() => setTab("templates")} />
            ) : (
              <div className="grid gap-4">
                {sequences.map((s) => (
                  <SequenceCard
                    key={s.id}
                    s={s}
                    onEdit={() => setEditing(s)}
                    onEnrollments={() => setEnrollFor(s)}
                    onAnalytics={() => setAnalyticsFor(s)}
                    onChange={load}
                    cap={limit}
                    activeCount={activeCount}
                  />
                ))}
              </div>
            )
          )}

          {/* Templates tab */}
          {tab === "templates" && (
            <TemplatesGrid
              templates={templates}
              onCloned={() => { setTab("sequences"); load(); }}
            />
          )}
        </>
      )}

      {/* Modals */}
      {editing && (
        <SequenceEditor
          sequence={editing === "new" ? null : (editing.aiDraft ? null : editing)}
          aiDraft={editing.aiDraft || null}
          aiAllowed={aiAllowed}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {enrollFor && (
        <EnrollmentsDrawer
          sequence={enrollFor}
          onClose={() => { setEnrollFor(null); load(); }}
        />
      )}
      {analyticsFor && (
        <AnalyticsDrawer
          sequence={analyticsFor}
          onClose={() => setAnalyticsFor(null)}
        />
      )}
      {showAI && (
        <AIGenerateModal
          onClose={() => setShowAI(false)}
          onDraft={(draft) => { setShowAI(false); setEditing({ aiDraft: draft }); }}
        />
      )}
    </div>
  );
}


// ─── Paywall ─────────────────────────────────────────────────────────────────
function Paywall() {
  const nav = useNavigate();
  return (
    <div
      className="card border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/60 to-white p-10"
      data-testid="sequences-paywall"
    >
      <Zap size={28} className="text-emerald-600 mb-3" />
      <h2 className="font-display font-bold text-2xl mb-2">Sequences are a Pro+ feature.</h2>
      <p className="text-[var(--ink-2)] text-lg max-w-2xl leading-relaxed mb-5">
        The fastest wholesalers win on speed-to-contact. Sequences fire SMS, Email and
        Call Tasks within minutes of a lead landing, auto-exit on reply, and branch when leads go cold.
      </p>
      <button type="button" className="btn-money" onClick={() => nav("/app/billing")} data-testid="sequences-upgrade-cta">
        Upgrade to Pro <ArrowRight size={14} />
      </button>
    </div>
  );
}


function Stat({ label, value, muted }) {
  return (
    <div className="leading-tight">
      <div className={"font-display font-bold text-lg " + (muted ? "text-[var(--ink-3)]" : "text-[var(--ink)]")}>{value}</div>
      <div className="text-xs text-[var(--ink-3)]">{label}</div>
    </div>
  );
}


function EmptyState({ onNew, onTemplates }) {
  return (
    <div className="card text-center py-16 px-6" data-testid="sequences-empty-state">
      <Zap size={36} className="text-emerald-600 mx-auto mb-3" />
      <h3 className="font-display font-bold text-xl mb-2">No sequences yet</h3>
      <p className="text-[var(--ink-2)] mb-6 max-w-lg mx-auto">
        Start with a prebuilt template like &quot;Speed-to-Contact (5 min rule)&quot; or design your own multi-channel cadence.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button type="button" className="btn-money" onClick={onTemplates} data-testid="empty-templates">
          <Layers size={14} /> Browse templates
        </button>
        <button type="button" className="btn-outline" onClick={onNew} data-testid="empty-new">
          <Plus size={14} /> Start from scratch
        </button>
      </div>
    </div>
  );
}


// ─── Sequence Card ──────────────────────────────────────────────────────────
function SequenceCard({ s, onEdit, onEnrollments, onAnalytics, onChange, cap, activeCount }) {
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (!s.enabled && cap !== -1 && activeCount >= cap) {
      toast.error(`Your plan allows ${cap} active sequence(s). Pause one or upgrade.`);
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/sequences/${s.id}`, { enabled: !s.enabled });
      toast.success(s.enabled ? "Paused" : "Activated");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to toggle");
    }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm("Delete this sequence? Active enrollments will be cancelled.")) return;
    setBusy(true);
    try {
      await api.delete(`/sequences/${s.id}`);
      toast.success("Deleted");
      onChange();
    } catch {
      toast.error("Delete failed");
    }
    setBusy(false);
  };

  const channelCounts = (s.steps || []).reduce((acc, st) => {
    acc[st.channel] = (acc[st.channel] || 0) + 1;
    return acc;
  }, {});
  const hasBranching = (s.steps || []).some((st) => st.branch_on_no_reply_hours);

  return (
    <div className="card p-5 hover:shadow-md transition-shadow" data-testid={`sequence-card-${s.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-lg truncate">{s.name}</h3>
            <span
              className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                s.enabled ? "bg-emerald-100 text-emerald-700" : "bg-[var(--bg-2)] text-[var(--ink-3)]"
              }`}
              data-testid={`sequence-status-${s.id}`}
            >
              {s.enabled ? "Active" : "Paused"}
            </span>
            {hasBranching && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                <GitBranch size={9} className="inline -mt-px" /> Branching
              </span>
            )}
            {s.ai_personalize && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                <Sparkles size={9} className="inline -mt-px" /> AI
              </span>
            )}
          </div>
          {s.description && <p className="text-sm text-[var(--ink-2)] mt-1 line-clamp-2">{s.description}</p>}
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          className={s.enabled ? "btn-outline text-amber-700 border-amber-300" : "btn-money"}
          data-testid={`sequence-toggle-${s.id}`}
        >
          {s.enabled ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Activate</>}
        </button>
      </div>

      {/* Step ribbon */}
      <div className="flex flex-wrap items-center gap-1 mb-3">
        {(s.steps || []).map((st, i) => {
          const meta = CHANNEL_META[st.channel] || CHANNEL_META.wait;
          const Icon = meta.icon;
          const delay = `${st.delay?.value || 0}${(st.delay?.unit || "h")[0]}`;
          return (
            <div key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={12} className="text-[var(--ink-3)]" />}
              <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded ${meta.color}`}>
                <Icon size={11} /> {delay}
              </span>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-[var(--ink-3)]">
        <div className="flex items-center gap-3">
          <span>{s.steps?.length || 0} steps</span>
          <span>·</span>
          <span>{s.active_enrollments} active</span>
          <span>·</span>
          <span>{s.total_enrollments} total</span>
          {Object.entries(channelCounts).map(([ch, n]) => {
            const meta = CHANNEL_META[ch];
            if (!meta) return null;
            const Icon = meta.icon;
            return (
              <span key={ch} className="inline-flex items-center gap-0.5"><Icon size={11} /> ×{n}</span>
            );
          })}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" className="btn-outline text-xs" onClick={onAnalytics} data-testid={`sequence-analytics-${s.id}`}>
            <BarChart3 size={12} /> Stats
          </button>
          <button type="button" className="btn-outline text-xs" onClick={onEnrollments} data-testid={`sequence-enrollments-${s.id}`}>
            <Users size={12} /> Enrollments
          </button>
          <button type="button" className="btn-outline text-xs" onClick={onEdit} data-testid={`sequence-edit-${s.id}`}>
            Edit
          </button>
          <button
            type="button"
            className="btn-outline text-xs text-red-700 border-red-200 hover:bg-red-50"
            onClick={remove}
            disabled={busy}
            data-testid={`sequence-delete-${s.id}`}
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Templates Grid ────────────────────────────────────────────────────────
function TemplatesGrid({ templates, onCloned }) {
  const [cloning, setCloning] = useState(null);

  const clone = async (key) => {
    setCloning(key);
    try {
      await api.post(`/sequences/templates/${key}/clone`);
      toast.success("Added to your sequences. Enable it to start.");
      onCloned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clone template");
    }
    setCloning(null);
  };

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="sequences-templates-grid">
      {templates.map((t) => {
        const channelCounts = (t.steps || []).reduce((acc, st) => {
          acc[st.channel] = (acc[st.channel] || 0) + 1;
          return acc;
        }, {});
        return (
          <div key={t.template_key} className="card p-5" data-testid={`template-card-${t.template_key}`}>
            <div className="flex items-start gap-3 mb-3">
              <div className="rounded-lg bg-emerald-50 p-2"><Zap size={18} className="text-emerald-600" /></div>
              <div className="flex-1 min-w-0">
                <h4 className="font-display font-bold text-lg">{t.name}</h4>
                <p className="text-sm text-[var(--ink-2)] mt-1">{t.description}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mb-3">
              {Object.entries(channelCounts).map(([ch, n]) => {
                const meta = CHANNEL_META[ch];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <span key={ch} className={`text-[10px] font-bold px-2 py-1 rounded ${meta.color}`}>
                    <Icon size={10} className="inline -mt-px" /> {meta.label} ×{n}
                  </span>
                );
              })}
              {t.triggers?.new_lead && (
                <span className="text-[10px] font-bold px-2 py-1 rounded bg-blue-100 text-blue-700">
                  Auto-fire on new lead
                </span>
              )}
            </div>
            <div className="text-xs text-[var(--ink-3)] mb-3">{t.steps?.length || 0} steps</div>
            <button
              type="button"
              className="btn-money w-full justify-center"
              onClick={() => clone(t.template_key)}
              disabled={cloning === t.template_key}
              data-testid={`template-clone-${t.template_key}`}
            >
              {cloning === t.template_key ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />}
              Add to my sequences
            </button>
          </div>
        );
      })}
    </div>
  );
}


// ─── Sequence Editor Modal ──────────────────────────────────────────────────
function SequenceEditor({ sequence, aiDraft, aiAllowed, onClose, onSaved }) {
  const seed = sequence || aiDraft || {};
  const [name, setName] = useState(seed.name || "");
  const [description, setDescription] = useState(seed.description || "");
  const [steps, setSteps] = useState(seed.steps && seed.steps.length ? [...seed.steps] : [
    { channel: "sms", delay: { value: 5, unit: "minutes" }, body: "" },
  ]);
  const [triggers, setTriggers] = useState(seed.triggers || { new_lead: false, sources: [], stages: [], manual: true });
  const [exitConditions, setExitConditions] = useState(seed.exit_conditions || {
    on_sms_reply: true, on_email_reply: true, on_stage_in: ["Under Contract", "Closed", "Dead"],
  });
  const [sendWindow, setSendWindow] = useState(seed.send_window || { start_hour: 9, end_hour: 19, weekdays_only: false });
  const [aiPersonalize, setAiPersonalize] = useState(!!seed.ai_personalize);
  const [saving, setSaving] = useState(false);

  const addStep = () => setSteps((arr) => [...arr, { channel: "sms", delay: { value: 1, unit: "days" }, body: "" }]);
  const removeStep = (i) => setSteps((arr) => arr.filter((_, idx) => idx !== i));
  const updateStep = (i, patch) => setSteps((arr) => arr.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const save = async () => {
    if (!name.trim()) { toast.error("Give it a name"); return; }
    if (!steps.length) { toast.error("Add at least one step"); return; }
    for (const [i, s] of steps.entries()) {
      if (s.channel === "sms" && !(s.body || "").trim()) { toast.error(`Step ${i + 1}: SMS body required`); return; }
      if (s.channel === "email" && !(s.body || "").trim()) { toast.error(`Step ${i + 1}: Email body required`); return; }
      if (s.channel === "task" && !(s.task_title || "").trim()) { toast.error(`Step ${i + 1}: Task title required`); return; }
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description,
        steps: steps.map((s) => ({
          channel: s.channel,
          delay: { value: Number(s.delay?.value) || 0, unit: s.delay?.unit || "hours" },
          subject: s.subject || "",
          body: s.body || "",
          task_title: s.task_title || "",
          task_notes: s.task_notes || "",
          task_priority: s.task_priority || "normal",
          branch_on_no_reply_hours: s.branch_on_no_reply_hours ? Number(s.branch_on_no_reply_hours) : null,
          branch_to_step_idx: s.branch_to_step_idx !== undefined && s.branch_to_step_idx !== null && s.branch_to_step_idx !== "" ? Number(s.branch_to_step_idx) : null,
          use_ai: !!s.use_ai,
        })),
        triggers,
        exit_conditions: exitConditions,
        send_window: sendWindow,
        ai_personalize: aiPersonalize && aiAllowed,
      };
      if (sequence?.id) {
        await api.patch(`/sequences/${sequence.id}`, payload);
        toast.success("Sequence updated");
      } else {
        await api.post("/sequences", payload);
        toast.success("Sequence created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  return (
    <ModalShell onClose={onClose} title={sequence ? "Edit Sequence" : (aiDraft ? "AI-Drafted Sequence" : "New Sequence")} testid="sequence-editor">
      <div className="space-y-5">
        {/* Basics */}
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} className="input" placeholder="e.g. New Lead 5-min Blitz" data-testid="editor-name" />
          </Field>
          <Field label="Description (optional)">
            <input value={description} onChange={(e) => setDescription(e.target.value)} className="input" placeholder="One-line goal" data-testid="editor-description" />
          </Field>
        </div>

        {/* Triggers */}
        <Section title="Triggers" subtitle="When should this sequence auto-start?">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="trigger-new-lead">
            <input type="checkbox" checked={triggers.new_lead} onChange={(e) => setTriggers((t) => ({ ...t, new_lead: e.target.checked }))} />
            <span>Auto-fire on every <strong>new lead</strong> created</span>
          </label>
          <div>
            <div className="text-xs text-[var(--ink-3)] mb-1 mt-2">Also trigger when lead source is:</div>
            <div className="flex flex-wrap gap-1">
              {SOURCES.map((src) => {
                const on = triggers.sources?.includes(src);
                return (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setTriggers((t) => ({
                      ...t,
                      sources: on ? (t.sources || []).filter((x) => x !== src) : [...(t.sources || []), src],
                    }))}
                    className={`text-[11px] font-bold px-2 py-1 rounded border ${on ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-[var(--ink-2)] border-[var(--line)]"}`}
                    data-testid={`trigger-source-${src.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {src}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--ink-3)] mb-1 mt-2">Also trigger when lead moves to stage:</div>
            <div className="flex flex-wrap gap-1">
              {STAGES.map((st) => {
                const on = triggers.stages?.includes(st);
                return (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setTriggers((t) => ({
                      ...t,
                      stages: on ? (t.stages || []).filter((x) => x !== st) : [...(t.stages || []), st],
                    }))}
                    className={`text-[11px] font-bold px-2 py-1 rounded border ${on ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-[var(--ink-2)] border-[var(--line)]"}`}
                    data-testid={`trigger-stage-${st.toLowerCase().replace(/\W+/g, "-")}`}
                  >
                    {st}
                  </button>
                );
              })}
            </div>
          </div>
        </Section>

        {/* Steps */}
        <Section title="Steps" subtitle="The cadence — mix SMS, Email and Call Tasks">
          <div className="space-y-3">
            {steps.map((s, i) => (
              <StepEditor
                key={i}
                idx={i}
                step={s}
                totalSteps={steps.length}
                onChange={(patch) => updateStep(i, patch)}
                onRemove={() => removeStep(i)}
              />
            ))}
            <button type="button" onClick={addStep} className="btn-outline w-full justify-center" data-testid="editor-add-step">
              <Plus size={14} /> Add step
            </button>
          </div>
        </Section>

        {/* Exit Conditions */}
        <Section title="Exit Conditions" subtitle="Auto-stop the sequence when…">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={exitConditions.on_sms_reply} onChange={(e) => setExitConditions((x) => ({ ...x, on_sms_reply: e.target.checked }))} />
            <span>Lead replies via <strong>SMS</strong></span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={exitConditions.on_email_reply} onChange={(e) => setExitConditions((x) => ({ ...x, on_email_reply: e.target.checked }))} />
            <span>Lead replies via <strong>Email</strong></span>
          </label>
          <div>
            <div className="text-xs text-[var(--ink-3)] mb-1 mt-2">Lead moves to stage:</div>
            <div className="flex flex-wrap gap-1">
              {STAGES.map((st) => {
                const on = exitConditions.on_stage_in?.includes(st);
                return (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setExitConditions((x) => ({
                      ...x,
                      on_stage_in: on ? (x.on_stage_in || []).filter((q) => q !== st) : [...(x.on_stage_in || []), st],
                    }))}
                    className={`text-[11px] font-bold px-2 py-1 rounded border ${on ? "bg-red-500 text-white border-red-500" : "bg-white text-[var(--ink-2)] border-[var(--line)]"}`}
                  >
                    {st}
                  </button>
                );
              })}
            </div>
          </div>
        </Section>

        {/* Send Window */}
        <Section title="Send Window" subtitle="Avoid 2am SMS that get reported as spam (UTC for now)">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Start hour">
              <input type="number" min="0" max="23" value={sendWindow.start_hour} onChange={(e) => setSendWindow((w) => ({ ...w, start_hour: Number(e.target.value) }))} className="input" data-testid="window-start" />
            </Field>
            <Field label="End hour">
              <input type="number" min="0" max="23" value={sendWindow.end_hour} onChange={(e) => setSendWindow((w) => ({ ...w, end_hour: Number(e.target.value) }))} className="input" data-testid="window-end" />
            </Field>
            <label className="flex items-center gap-2 text-sm mt-7">
              <input type="checkbox" checked={sendWindow.weekdays_only} onChange={(e) => setSendWindow((w) => ({ ...w, weekdays_only: e.target.checked }))} />
              <span>Weekdays only</span>
            </label>
          </div>
        </Section>

        {/* AI Personalization */}
        {aiAllowed && (
          <Section title="AI Personalization" subtitle="Claude rewrites every step's opener using lead data">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={aiPersonalize} onChange={(e) => setAiPersonalize(e.target.checked)} data-testid="editor-ai-toggle" />
              <span>Enable AI personalization for every step</span>
            </label>
          </Section>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--line)]">
          <button type="button" className="btn-outline" onClick={onClose} data-testid="editor-cancel">Cancel</button>
          <button type="button" className="btn-money" onClick={save} disabled={saving} data-testid="editor-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sequence ? "Save changes" : "Create sequence"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}


function StepEditor({ idx, step, totalSteps, onChange, onRemove }) {
  const meta = CHANNEL_META[step.channel] || CHANNEL_META.sms;
  const Icon = meta.icon;
  return (
    <div className={`rounded-xl border-l-4 ${meta.accent} bg-[var(--bg-2)] p-3`} data-testid={`step-editor-${idx}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[10px] font-bold uppercase text-[var(--ink-3)]">Step {idx + 1}</span>
        <select
          value={step.channel}
          onChange={(e) => onChange({ channel: e.target.value })}
          className="input !py-1 !text-sm !w-auto"
          data-testid={`step-${idx}-channel`}
        >
          <option value="sms">SMS</option>
          <option value="email">Email</option>
          <option value="task">Call Task</option>
          <option value="wait">Wait only</option>
        </select>
        <span className="text-xs text-[var(--ink-3)]">after</span>
        <input
          type="number"
          min="0"
          value={step.delay?.value ?? 0}
          onChange={(e) => onChange({ delay: { ...step.delay, value: Number(e.target.value), unit: step.delay?.unit || "hours" } })}
          className="input !py-1 !text-sm !w-20"
          data-testid={`step-${idx}-delay-value`}
        />
        <select
          value={step.delay?.unit || "hours"}
          onChange={(e) => onChange({ delay: { ...step.delay, unit: e.target.value, value: step.delay?.value || 0 } })}
          className="input !py-1 !text-sm !w-auto"
          data-testid={`step-${idx}-delay-unit`}
        >
          <option value="minutes">min</option>
          <option value="hours">hours</option>
          <option value="days">days</option>
        </select>
        <div className="flex-1" />
        <Icon size={16} className="text-[var(--ink-3)]" />
        {totalSteps > 1 && (
          <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-700 p-1" data-testid={`step-${idx}-remove`}>
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {step.channel === "sms" && (
        <textarea
          value={step.body || ""}
          onChange={(e) => onChange({ body: e.target.value })}
          rows={3}
          maxLength={1500}
          placeholder="Hi {first_name}, this is {agent_name} re: {property_address}…  Reply STOP to opt out."
          className="input"
          data-testid={`step-${idx}-body`}
        />
      )}

      {step.channel === "email" && (
        <>
          <input
            value={step.subject || ""}
            onChange={(e) => onChange({ subject: e.target.value })}
            placeholder="Subject line — use {property_address}, {first_name}"
            className="input mb-2"
            data-testid={`step-${idx}-subject`}
          />
          <textarea
            value={step.body || ""}
            onChange={(e) => onChange({ body: e.target.value })}
            rows={5}
            placeholder="Hi {first_name},&#10;&#10;Following up about {property_address}…"
            className="input"
            data-testid={`step-${idx}-body`}
          />
        </>
      )}

      {step.channel === "task" && (
        <>
          <input
            value={step.task_title || ""}
            onChange={(e) => onChange({ task_title: e.target.value })}
            placeholder="Task title — e.g. Call {first_name} re: {property_address}"
            className="input mb-2"
            data-testid={`step-${idx}-task-title`}
          />
          <textarea
            value={step.task_notes || ""}
            onChange={(e) => onChange({ task_notes: e.target.value })}
            rows={2}
            placeholder="Notes / script reminder for whoever picks up the task"
            className="input mb-2"
            data-testid={`step-${idx}-task-notes`}
          />
          <select
            value={step.task_priority || "normal"}
            onChange={(e) => onChange({ task_priority: e.target.value })}
            className="input !w-auto"
            data-testid={`step-${idx}-task-priority`}
          >
            <option value="low">Low priority</option>
            <option value="normal">Normal priority</option>
            <option value="high">High priority</option>
          </select>
        </>
      )}

      {/* Branching — only meaningful for SMS/Email and when there's a later step */}
      {(step.channel === "sms" || step.channel === "email") && idx < totalSteps - 1 && (
        <div className="mt-3 pt-3 border-t border-dashed border-[var(--line)]">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--ink-3)] mb-2">
            <GitBranch size={10} className="inline -mt-px" /> Branch on no-reply (optional)
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-[var(--ink-2)]">If no reply within</span>
            <input
              type="number"
              min="0"
              value={step.branch_on_no_reply_hours || ""}
              onChange={(e) => onChange({ branch_on_no_reply_hours: e.target.value ? Number(e.target.value) : null })}
              className="input !py-1 !text-xs !w-20"
              placeholder="hours"
              data-testid={`step-${idx}-branch-hours`}
            />
            <span className="text-[var(--ink-2)]">hours, jump to step</span>
            <select
              value={step.branch_to_step_idx ?? ""}
              onChange={(e) => onChange({ branch_to_step_idx: e.target.value === "" ? null : Number(e.target.value) })}
              className="input !py-1 !text-xs !w-auto"
              data-testid={`step-${idx}-branch-target`}
            >
              <option value="">— none —</option>
              {Array.from({ length: totalSteps }, (_, i) => i).filter((i) => i !== idx).map((i) => (
                <option key={i} value={i}>{`Step ${i + 1}`}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}


function Section({ title, subtitle, children }) {
  return (
    <div className="space-y-2 pt-1">
      <div>
        <div className="font-bold text-sm">{title}</div>
        {subtitle && <div className="text-xs text-[var(--ink-3)]">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-bold text-[var(--ink-3)]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}


// ─── Modal shell ────────────────────────────────────────────────────────────
function ModalShell({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid={testid}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mt-8 mb-8">
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
          <h3 className="font-display font-bold text-xl">{title}</h3>
          <button type="button" onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)] p-1" data-testid={`${testid}-close`}>
            <X size={18} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}


// ─── Enrollments Drawer ────────────────────────────────────────────────────
function EnrollmentsDrawer({ sequence, onClose }) {
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [leadQuery, setLeadQuery] = useState("");
  const [allLeads, setAllLeads] = useState([]);
  const [showLeadPicker, setShowLeadPicker] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sequences/${sequence.id}/enrollments`);
      setEnrollments(data.enrollments || []);
    } catch (e) {
      toast.error("Could not load enrollments");
    }
    setLoading(false);
  }, [sequence.id]);

  const loadLeads = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads`);
      setAllLeads(Array.isArray(data) ? data : []);
    } catch {
      // best-effort
    }
  }, []);

  useEffect(() => { load(); loadLeads(); }, [load, loadLeads]);

  const act = async (id, action) => {
    try {
      await api.post(`/sequences/enrollments/${id}/${action}`);
      toast.success("Done");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const enrollLead = async (leadId) => {
    setEnrolling(true);
    try {
      await api.post(`/sequences/enroll`, { sequence_id: sequence.id, lead_id: leadId });
      toast.success("Lead enrolled — first step will run shortly");
      setShowLeadPicker(false);
      setLeadQuery("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not enroll");
    }
    setEnrolling(false);
  };

  const filteredLeads = useMemo(() => {
    const q = (leadQuery || "").toLowerCase().trim();
    if (!q) return allLeads.slice(0, 30);
    return allLeads.filter((l) =>
      (l.property_address || "").toLowerCase().includes(q) ||
      (l.seller_name || "").toLowerCase().includes(q) ||
      (l.city || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [leadQuery, allLeads]);

  return (
    <ModalShell onClose={onClose} title={`Enrollments — ${sequence.name}`} testid="enrollments-drawer">
      <div className="mb-4">
        {!showLeadPicker ? (
          <button type="button" className="btn-money" onClick={() => setShowLeadPicker(true)} data-testid="enroll-lead-btn">
            <UserPlus size={14} /> Enroll a lead manually
          </button>
        ) : (
          <div className="card p-3 bg-emerald-50/40">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wider">Pick a lead to enroll</div>
              <button type="button" onClick={() => { setShowLeadPicker(false); setLeadQuery(""); }} className="text-[var(--ink-3)] hover:text-[var(--ink)]">
                <X size={14} />
              </button>
            </div>
            <input
              autoFocus
              value={leadQuery}
              onChange={(e) => setLeadQuery(e.target.value)}
              placeholder="Search by address, name or city…"
              className="input mb-2"
              data-testid="enroll-lead-search"
            />
            <div className="max-h-48 overflow-y-auto space-y-1">
              {filteredLeads.length === 0 ? (
                <div className="text-xs text-[var(--ink-3)] py-2">No leads found.</div>
              ) : filteredLeads.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => enrollLead(l.id)}
                  disabled={enrolling}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-white border border-transparent hover:border-[var(--line)] text-sm flex items-center justify-between gap-2"
                  data-testid={`enroll-lead-pick-${l.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate">{l.property_address || "—"}</div>
                    <div className="text-[10px] text-[var(--ink-3)] truncate">
                      {l.seller_name && <span>{l.seller_name} · </span>}
                      {l.city}, {l.state} {l.zip_code} · {l.stage}
                    </div>
                  </div>
                  <ArrowRight size={12} className="text-[var(--ink-3)] shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-10 text-[var(--ink-3)]"><Loader2 size={20} className="animate-spin mx-auto" /></div>
      ) : enrollments.length === 0 ? (
        <div className="text-center py-10 text-[var(--ink-3)]">
          <Users size={28} className="mx-auto mb-2 opacity-40" />
          <p>No enrollments yet. Enable this sequence + create matching leads.</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {enrollments.map((e) => {
            const status = STATUS_META[e.status] || { label: e.status, color: "text-[var(--ink-3)] bg-[var(--bg-2)]" };
            return (
              <div key={e.id} className="card p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`enrollment-${e.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm truncate">
                    {e.merge?.property_address || e.recipient_email || e.recipient_phone || e.lead_id}
                  </div>
                  <div className="text-xs text-[var(--ink-3)] truncate">
                    {e.recipient_phone && <span>{e.recipient_phone}</span>}
                    {e.recipient_phone && e.recipient_email && <span> · </span>}
                    {e.recipient_email && <span>{e.recipient_email}</span>}
                  </div>
                  <div className="text-[10px] mt-1">
                    Step {e.current_step + 1}/{sequence.steps?.length || 0} · {e.sent_count || 0} actions sent
                  </div>
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded ${status.color}`}>{status.label}</span>
                <div className="flex items-center gap-1">
                  {e.status === "active" && (
                    <>
                      <button type="button" className="btn-outline text-xs" onClick={() => act(e.id, "pause")}><Pause size={11} /></button>
                      <button type="button" className="btn-outline text-xs" onClick={() => act(e.id, "skip")}><SkipForward size={11} /></button>
                    </>
                  )}
                  {e.status === "paused" && (
                    <button type="button" className="btn-money text-xs" onClick={() => act(e.id, "resume")}><Play size={11} /></button>
                  )}
                  {(e.status === "active" || e.status === "paused") && (
                    <button type="button" className="btn-outline text-xs text-red-700 border-red-200" onClick={() => act(e.id, "cancel")}><CircleSlash size={11} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </ModalShell>
  );
}


// ─── Analytics Drawer ──────────────────────────────────────────────────────
function AnalyticsDrawer({ sequence, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sequences/${sequence.id}/analytics`);
        setData(data);
      } catch (e) {
        toast.error("Could not load analytics");
      }
      setLoading(false);
    })();
  }, [sequence.id]);

  return (
    <ModalShell onClose={onClose} title={`Analytics — ${sequence.name}`} testid="analytics-drawer">
      {loading ? (
        <div className="text-center py-10"><Loader2 size={20} className="animate-spin mx-auto" /></div>
      ) : !data ? (
        <div className="text-center py-10 text-[var(--ink-3)]">No data yet.</div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <BigStat label="Enrolled" value={data.summary.total_enrolled} />
            <BigStat label="Active" value={data.summary.active} muted />
            <BigStat label="Reply rate" value={`${data.summary.reply_rate}%`} good={data.summary.reply_rate > 5} />
            <BigStat label="Completion" value={`${data.summary.completion_rate}%`} />
          </div>

          {data.per_step.length > 0 && (
            <div>
              <h4 className="font-bold text-sm mb-2">Per-step results</h4>
              <div className="space-y-1 text-sm">
                {data.per_step.map((p) => (
                  <div key={p.step} className="flex flex-wrap items-center gap-3 px-3 py-2 bg-[var(--bg-2)] rounded-lg">
                    <span className="font-bold">Step {p.step + 1}</span>
                    <span className="text-emerald-700">{p.sent_email || 0} email</span>
                    <span className="text-blue-700">{p.sent_sms || 0} sms</span>
                    <span className="text-amber-700">{p.created_task || 0} tasks</span>
                    {(p.email_failed || p.sms_failed) && (
                      <span className="text-red-700">{(p.email_failed || 0) + (p.sms_failed || 0)} failed</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-xs text-[var(--ink-3)] flex items-start gap-2">
            <AlertCircle size={12} className="mt-0.5 shrink-0" />
            <span>Reply rate counts enrollments that exited because the lead replied via SMS or email. Higher = your messaging hits the mark.</span>
          </div>
        </div>
      )}
    </ModalShell>
  );
}


function BigStat({ label, value, good, muted }) {
  return (
    <div className="card p-3">
      <div className={`font-display font-black text-2xl ${good ? "text-emerald-600" : muted ? "text-[var(--ink-3)]" : "text-[var(--ink)]"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--ink-3)] mt-1">{label}</div>
    </div>
  );
}


// ─── AI Generate Modal ─────────────────────────────────────────────────────
function AIGenerateModal({ onClose, onDraft }) {
  const [prompt, setPrompt] = useState("");
  const [audience, setAudience] = useState("seller");
  const [aggressiveness, setAggressiveness] = useState("medium");
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!prompt.trim() || prompt.trim().length < 10) {
      toast.error("Describe what you want in more detail");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/sequences/ai-generate", { prompt: prompt.trim(), audience, aggressiveness });
      toast.success("AI drafted your sequence — review and tweak below");
      onDraft(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    }
    setGenerating(false);
  };

  return (
    <ModalShell onClose={onClose} title="AI Sequence Builder" testid="ai-modal">
      <div className="space-y-4">
        <p className="text-sm text-[var(--ink-2)]">
          Describe the cadence you want — Claude will draft steps across SMS, Email and Tasks.
          You&apos;ll be able to edit every line before saving.
        </p>

        <Field label="Describe your sequence">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            className="input"
            placeholder='e.g. "Aggressive 5-touch blitz for new pre-foreclosure leads. Empathy first SMS, then call task day 1, email day 3, final SMS day 7"'
            data-testid="ai-prompt"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Audience">
            <select value={audience} onChange={(e) => setAudience(e.target.value)} className="input" data-testid="ai-audience">
              <option value="seller">Motivated Seller</option>
              <option value="buyer">Cash Buyer</option>
              <option value="probate">Probate</option>
              <option value="post-close">Post-Close</option>
            </select>
          </Field>
          <Field label="Aggressiveness">
            <select value={aggressiveness} onChange={(e) => setAggressiveness(e.target.value)} className="input" data-testid="ai-aggressiveness">
              <option value="gentle">Gentle</option>
              <option value="medium">Medium</option>
              <option value="aggressive">Aggressive</option>
            </select>
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--line)]">
          <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-money" onClick={generate} disabled={generating} data-testid="ai-generate-btn">
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            Generate draft
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
